import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ContentTraverseNodeType, isLocationItemField, } from '@dossierhq/core';
import { Dialog2, FullscreenContainer, IconButton, Input, Text, toSizeClassName, } from '@dossierhq/design';
import { MapContainer } from '@dossierhq/leaflet';
import { useCallback, useContext, useEffect, useMemo, useReducer, useState, } from 'react';
import { DossierContext } from '../../contexts/DossierContext.js';
import { EntityEditorStateContext } from '../../contexts/EntityEditorStateContext.js';
import { useAdminLoadEntitySearch } from '../../hooks/useAdminLoadEntitySearch.js';
import { traverseEntityEditorDraft } from '../../reducers/EntityEditorReducer/EntityDraftTraverser.js';
import { initializeSearchEntityState, reduceSearchEntityState, SearchEntityStateActions, } from '../../reducers/SearchEntityReducer/SearchEntityReducer.js';
import { AdminEntityMapMarker } from '../AdminEntityMapMarker/AdminEntityMapMarker.js';
import { EntityMap } from '../EntityMap/EntityMap.js';
import { EntityDraftMapMarker } from './EntityDraftMapMarker.js';
import { initializeLocationState, reduceLocation, stringsToLocation } from './LocationReducer.js';
export function AdminLocationSelectorDialog({ title, value, onChange, onItemClick, }) {
    return (_jsx(Dialog2, { width: "wide", height: "fill", children: ({ close }) => (_jsxs(FullscreenContainer, { card: true, height: "100%", children: [_jsxs(FullscreenContainer.Row, { flexDirection: "row", alignItems: "center", children: [_jsx(FullscreenContainer.Item, { flexGrow: 1, paddingHorizontal: 3, paddingVertical: 2, children: _jsx(Text, { textStyle: "headline5", children: title }) }), _jsx(IconButton, { icon: "close", color: "white", onClick: close })] }), _jsx(Content, { value: value, onChange: onChange, onItemClick: onItemClick })] })) }));
}
function Content({ value, onChange, onItemClick, }) {
    const { schema } = useContext(DossierContext);
    const entityEditorState = useContext(EntityEditorStateContext);
    // Lat, lng state
    const [{ latString, lngString }, dispatch] = useReducer(reduceLocation, value, initializeLocationState);
    useEffect(() => {
        dispatch({ type: 'value', value });
    }, [value]);
    const emitIfChanged = useCallback((nextLatString, nextLngString) => {
        const location = stringsToLocation({ latString: nextLatString, lngString: nextLngString });
        if (location && (!value || location.lat !== value.lat || location.lng !== value.lng)) {
            onChange(location);
        }
    }, [value, onChange]);
    const handleLatChange = useCallback((event) => {
        const nextLat = event.currentTarget.value;
        dispatch({ type: 'lat', value: nextLat });
        emitIfChanged(nextLat, lngString);
    }, [emitIfChanged, lngString]);
    const handleLngChange = useCallback((event) => {
        const nextLng = event.currentTarget.value;
        dispatch({ type: 'lng', value: nextLng });
        emitIfChanged(latString, nextLng);
    }, [emitIfChanged, latString]);
    // Reset signal
    const [resetSignal, setResetSignal] = useState(0);
    const handleResetClick = useCallback(() => setResetSignal((it) => it + 1), []);
    // Entity search state
    const [searchEntityState, dispatchSearchEntityState] = useReducer(reduceSearchEntityState, { mode: 'admin', actions: [new SearchEntityStateActions.SetSampling({ count: 100 }, false)] }, initializeSearchEntityState);
    useAdminLoadEntitySearch(searchEntityState, dispatchSearchEntityState);
    // Draft locations
    const { draftIds, markers } = useMemo(() => extractDraftLocations(schema, entityEditorState.drafts, value), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [schema, entityEditorState.drafts]);
    const filterEntity = useCallback((entity) => !draftIds.has(entity.id), [draftIds]);
    return (_jsxs(_Fragment, { children: [_jsxs(FullscreenContainer.Row, { center: true, flexDirection: "row", gap: 2, paddingVertical: 2, children: [_jsx(Input, { value: latString, onChange: handleLatChange, type: "number", min: -90.0, max: 90.0, step: 0.000001 }), _jsx(Input, { value: lngString, onChange: handleLngChange, type: "number", min: -180.0, max: 180.0, step: 0.000001 }), _jsx(IconButton, { disabled: !value, icon: "location", onClick: handleResetClick })] }), _jsx(FullscreenContainer.Row, { fillHeight: true, fullWidth: true, children: _jsxs(EntityMap, { className: toSizeClassName({ height: '100%' }), schema, searchEntityState, dispatchSearchEntityState, filterEntity: filterEntity, renderEntityMarker: (key, entity, location) => (_jsx(AdminEntityMapMarker, { entity: entity, location: location, onClick: onItemClick ? () => onItemClick(entity) : undefined }, key)), center: value, resetSignal: resetSignal, children: [markers.map((marker, index) => (_jsx(EntityDraftMapMarker, { draftState: marker.draftState, location: marker.location, onClick: onItemClick ? () => onItemClick(marker.draftState) : undefined }, index))), _jsx(MapContainer.EditLocationMarker, { value: value, onChange: onChange })] }) })] }));
}
function extractDraftLocations(schema, drafts, value) {
    const draftIds = new Set();
    const markers = [];
    function considerLocation(draftState, location) {
        if (value && value.lat === location.lat && value.lng === location.lat) {
            return;
        }
        markers.push({ draftState, location });
    }
    for (const draftState of drafts) {
        draftIds.add(draftState.id);
        if (schema) {
            for (const node of traverseEntityEditorDraft(schema, draftState)) {
                if (node.type === ContentTraverseNodeType.fieldItem) {
                    if (isLocationItemField(node.fieldSpec, node.value) && node.value) {
                        considerLocation(draftState, node.value);
                    }
                }
            }
        }
    }
    return { draftIds, markers };
}
//# sourceMappingURL=AdminLocationSelectorDialog.js.map